/**
 * ============================================================================
 * PostActionTracker — Phase 6c frontend (CCIE — Change Control Impact Engine).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /changes/:id/post-actions
 * Purpose: surfaces the post-action queue for an approved CC. Each impact
 *          item becomes a post-action that drains through QUEUED → RUNNING →
 *          COMPLETED or FAILED. Failed actions can be re-enqueued via the
 *          inline Retry button.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - Sankey-lite pipeline graphic at the top so the operator sees the queue
 *     shape at a glance.
 *   - Table with caption + scope; the polling cadence (5 s) is announced in
 *     the subtitle so the user knows progress is live.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './PostActionTracker.module.css';
import { usePostActionsQuery } from './usePostActionsQuery';
import { StatusPipeline } from './StatusPipeline';
import { PostActionRow } from './PostActionRow';

export interface PostActionTrackerProps {
  /** Optional override so the screen renders standalone without a router. */
  changeId?: string;
}

export function PostActionTracker({ changeId = 'cc-demo' }: PostActionTrackerProps = {}) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'medium',
      }),
    [i18n.language],
  );

  const postActionsQ = usePostActionsQuery(changeId);
  const data = postActionsQ.data;
  const items = data?.items ?? [];
  const pipeline = data?.pipeline ?? {
    QUEUED: 0,
    RUNNING: 0,
    COMPLETED: 0,
    FAILED: 0,
  };
  const total =
    pipeline.QUEUED + pipeline.RUNNING + pipeline.COMPLETED + pipeline.FAILED;

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('changeControl.postActions.eyebrow', {
          defaultValue: 'Quality Events',
        })}
        title={t('changeControl.postActions.title', {
          defaultValue: 'Post-action tracker',
        })}
        subtitle={t('changeControl.postActions.subtitle', {
          defaultValue:
            'Live queue of side-effect actions enqueued by this change. Refreshes every 5 seconds.',
        })}
      />

      {postActionsQ.isLoading && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {postActionsQ.isError && (
        <Card>
          <CardBody>
            <div role="alert" className={styles.errorBanner}>
              <span>
                {t('changeControl.postActions.errors.loadFailed', {
                  defaultValue: 'Could not load the post-action queue.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => postActionsQ.refetch()}
              >
                {t('changeControl.postActions.actions.retry', {
                  defaultValue: 'Retry',
                })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {!postActionsQ.isLoading && !postActionsQ.isError && data && (
        <>
          <Card>
            <CardHeader>
              <div className={styles.cardHeaderRow}>
                <h2 className={wstyles.sectionTitle}>
                  {t('changeControl.postActions.pipeline.title', {
                    defaultValue: 'Pipeline',
                  })}{' '}
                  <span className={styles.muted}>({nf.format(total)})</span>
                </h2>
                <span className={styles.generatedAt}>
                  {t('changeControl.postActions.generatedAt', {
                    defaultValue: 'Refreshed {{ts}}',
                    ts: dateFmt.format(new Date(data.generated_at_utc)),
                  })}
                </span>
              </div>
            </CardHeader>
            <CardBody>
              <StatusPipeline counts={pipeline} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className={wstyles.sectionTitle}>
                {t('changeControl.postActions.queue.title', {
                  defaultValue: 'Queue',
                })}{' '}
                <span className={styles.muted}>({nf.format(items.length)})</span>
              </h2>
            </CardHeader>
            <CardBody>
              {items.length === 0 ? (
                <p className={styles.emptyState}>
                  {t('changeControl.postActions.empty', {
                    defaultValue:
                      'No post-actions enqueued. The change either has no side effects or has not been approved yet.',
                  })}
                </p>
              ) : (
                <div className={styles.tableScroll}>
                  <table
                    className={wstyles.table}
                    aria-label={t('changeControl.postActions.tableAria', {
                      defaultValue: 'Post-action queue',
                    })}
                  >
                    <caption className={styles.srOnly}>
                      {t('changeControl.postActions.tableCaption', {
                        defaultValue:
                          'Post-action queue showing action type, target entity, status, timestamps, and retry count.',
                      })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">
                          {t('changeControl.postActions.columns.actionType', {
                            defaultValue: 'Action type',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.target', {
                            defaultValue: 'Target entity',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.status', {
                            defaultValue: 'Status',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.queuedAt', {
                            defaultValue: 'Queued at',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.completedAt', {
                            defaultValue: 'Completed at',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.retryCount', {
                            defaultValue: 'Retries',
                          })}
                        </th>
                        <th scope="col">
                          {t('changeControl.postActions.columns.actions', {
                            defaultValue: 'Actions',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((a) => (
                        <PostActionRow
                          key={a.action_id}
                          changeId={changeId}
                          action={a}
                          onRetried={() => postActionsQ.refetch()}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
